import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { useQontoConnectionStatus } from '@/hooks/useQontoOAuth'
import { useQontoQuotes, useQontoInvoices } from '@/hooks/useQontoData'
import type { QontoQuote, QontoInvoice } from '@/hooks/useQontoData'
import { formatCurrency, formatDate } from '@/lib/utils'
import { Landmark, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'

interface QontoSyncBannerProps {
  type: 'quotes' | 'invoices'
}

function getStatusBadge(status: string): { label: string; variant: 'mint' | 'gold' | 'beige' | 'red' | 'default' } {
  const normalized = status.toLowerCase()

  if (['paid', 'payee', 'completed', 'accepted'].includes(normalized)) {
    return { label: 'Paye', variant: 'mint' }
  }
  if (['pending', 'unpaid', 'sent', 'en_attente', 'outstanding'].includes(normalized)) {
    return { label: 'En attente', variant: 'gold' }
  }
  if (['draft'].includes(normalized)) {
    return { label: 'Brouillon', variant: 'beige' }
  }
  if (['cancelled', 'rejected', 'overdue'].includes(normalized)) {
    return { label: normalized === 'overdue' ? 'En retard' : 'Annule', variant: 'red' }
  }
  return { label: status, variant: 'default' }
}

function formatAmount(amount: { value: string; currency: string } | undefined): string {
  if (!amount) return '-'
  const num = parseFloat(amount.value)
  if (isNaN(num)) return '-'
  return formatCurrency(num)
}

export function QontoSyncBanner({ type }: QontoSyncBannerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { data: status } = useQontoConnectionStatus()

  const quotesQuery = useQontoQuotes()
  const invoicesQuery = useQontoInvoices()

  const isConnected = !!status?.connected

  if (!isConnected) return null

  const isQuotes = type === 'quotes'
  const query = isQuotes ? quotesQuery : invoicesQuery
  const items = isQuotes
    ? (quotesQuery.data?.quotes ?? [])
    : (invoicesQuery.data?.client_invoices ?? [])
  const isLoading = query.isLoading
  const isError = query.isError
  const title = isQuotes ? 'Devis Qonto' : 'Factures Qonto'
  const count = items.length

  return (
    <Card padding="none" className="border border-beige-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-beige-50 transition-colors rounded-xl"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-mint-100 flex items-center justify-center">
            <Landmark className="w-4 h-4 text-mint-600" />
          </div>
          <span className="text-sm font-medium text-brown-900">{title}</span>
          {!isLoading && (
            <Badge variant="beige" size="sm">
              {count}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-brown-400">
          {isLoading && <Spinner className="w-4 h-4" />}
          {isOpen ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-beige-200">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : isError ? (
            <div className="text-center py-6 text-brown-500 text-sm">
              Erreur lors du chargement des donnees Qonto
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-6 text-brown-500 text-sm">
              Aucun {isQuotes ? 'devis' : 'e facture'} sur Qonto
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-beige-50">
                  <tr>
                    <th className="text-left px-5 py-2 text-xs font-medium text-brown-500 uppercase tracking-wider">
                      Numero
                    </th>
                    <th className="text-right px-5 py-2 text-xs font-medium text-brown-500 uppercase tracking-wider">
                      Montant
                    </th>
                    <th className="text-left px-5 py-2 text-xs font-medium text-brown-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="text-left px-5 py-2 text-xs font-medium text-brown-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="text-right px-5 py-2 text-xs font-medium text-brown-500 uppercase tracking-wider">
                      Lien
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-beige-100">
                  {isQuotes
                    ? (items as QontoQuote[]).map((item) => {
                        const badge = getStatusBadge(item.status)
                        const url = item.quote_url
                        return (
                          <tr key={item.id} className="hover:bg-beige-50 transition-colors">
                            <td className="px-5 py-3 text-sm font-medium text-brown-900">
                              {item.number || '-'}
                            </td>
                            <td className="px-5 py-3 text-sm text-brown-900 text-right font-medium">
                              {formatAmount(item.total_amount)}
                            </td>
                            <td className="px-5 py-3">
                              <Badge variant={badge.variant} size="sm">
                                {badge.label}
                              </Badge>
                            </td>
                            <td className="px-5 py-3 text-sm text-brown-600">
                              {item.issue_date ? formatDate(item.issue_date) : '-'}
                            </td>
                            <td className="px-5 py-3 text-right">
                              {url && (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-mint-600 hover:text-mint-700 text-sm"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </td>
                          </tr>
                        )
                      })
                    : (items as QontoInvoice[]).map((item) => {
                        const badge = getStatusBadge(item.status)
                        const url = item.invoice_url
                        return (
                          <tr key={item.id} className="hover:bg-beige-50 transition-colors">
                            <td className="px-5 py-3 text-sm font-medium text-brown-900">
                              {item.number || '-'}
                            </td>
                            <td className="px-5 py-3 text-sm text-brown-900 text-right font-medium">
                              {formatAmount(item.total_amount)}
                            </td>
                            <td className="px-5 py-3">
                              <Badge variant={badge.variant} size="sm">
                                {badge.label}
                              </Badge>
                            </td>
                            <td className="px-5 py-3 text-sm text-brown-600">
                              {item.issue_date ? formatDate(item.issue_date) : '-'}
                            </td>
                            <td className="px-5 py-3 text-right">
                              {url && (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-mint-600 hover:text-mint-700 text-sm"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
